import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import MapPickerModal from './MapPickerModal';
import { BACKEND_URL } from '../config/api';

const AddLocationModal = ({ 
  children, 
  onLocationAdded, 
  isOpen: externalIsOpen, 
  onClose: externalOnClose, 
  onAuthRequired, 
  initialMapCenter = null,
  initialMapZoom = null,
  openDirectToMapPicker = false
}) => {
  console.log('🚀 AddLocationModal component mounted/rendered');
  console.log('📍 Initial map center received:', initialMapCenter);
  console.log('🔍 Initial map zoom received:', initialMapZoom);
  console.log('🗺️ Open direct to map picker:', openDirectToMapPicker);
  const navigate = useNavigate();
  
  // Debug: Log all clicks to track navigation trigger
  useEffect(() => {
    const clickLogger = (e) => {
      console.log('🖱️ Click detected on:', e.target.tagName, e.target.className);
      console.log('   Current URL:', window.location.href);
      
      // Prevent clicks on Google Places autocomplete from closing the modal
      if (e.target.closest('.pac-container')) {
        console.log('✋ Click on .pac-container detected - preventing modal close');
        e.stopPropagation();
      }
    };
    // Use bubble phase (false) instead of capture phase to avoid blocking React events
    document.addEventListener('click', clickLogger, false);
    return () => document.removeEventListener('click', clickLogger, false);
  }, []);
  
  const [isOpen, setIsOpen] = useState(false);
  
  // Use external state if provided, otherwise use internal state
  const modalIsOpen = externalIsOpen !== undefined ? externalIsOpen : isOpen;
  const handleClose = () => {
    // Check if main modal close is prevented (when exit modal should show)
    if (preventMainClose) {
      console.log('✋ handleClose prevented - exit modal is handling closure');
      return;
    }
    
    if (externalOnClose) {
      externalOnClose();
    } else {
      setIsOpen(false);
    }
  };
  const [isLoading, setIsLoading] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const addressInputRef = useRef(null);
  const addressAutocompleteRef = useRef(null);
  const hasAutoOpenedMapPicker = useRef(false); // Track if map picker was auto-opened

  const { user, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    address: '',
    amenities: [],
    image_url: '',
    location: null, // Will store {country, city, suburb, region}
    pinCategory: 'standard' // Pin type: standard (default), featured, admin_a, admin_b, admin_c, admin_d
  });

  const [amenityInput, setAmenityInput] = useState('');
  const [coordinates, setCoordinates] = useState(initialMapCenter);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [preventMainClose, setPreventMainClose] = useState(false);

  // Update coordinates when initialMapCenter is provided and modal opens
  useEffect(() => {
    if (modalIsOpen && initialMapCenter && !coordinates) {
      console.log('📍 Setting coordinates from initialMapCenter:', initialMapCenter);
      setCoordinates(initialMapCenter);
      setFormData(prev => ({
        ...prev,
        latitude: initialMapCenter.lat.toString(),
        longitude: initialMapCenter.lng.toString()
      }));
    }
  }, [modalIsOpen, initialMapCenter, coordinates]);

  // Auto-open map picker when opening directly from main map (only once)
  useEffect(() => {
    if (modalIsOpen && openDirectToMapPicker && !showMapPicker && !hasAutoOpenedMapPicker.current) {
      console.log('🗺️ Auto-opening map picker (direct from main map)');
      hasAutoOpenedMapPicker.current = true; // Mark as opened
      // Small delay to ensure modal is mounted
      setTimeout(() => {
        setShowMapPicker(true);
      }, 100);
    }
  }, [modalIsOpen, openDirectToMapPicker, showMapPicker]);

  // Initialize address autocomplete when modal opens
  useEffect(() => {
    if (modalIsOpen && addressInputRef.current && window.google?.maps?.places && !addressAutocompleteRef.current) {
      try {
        console.log('🔍 Initializing address autocomplete...');
        
        // TODO [Q2-Q3 2025]: Migrate to PlaceAutocompleteElement when Google issues 12-month warning
        // Current API: google.maps.places.Autocomplete (deprecated Mar 2025 for NEW customers only)
        // Migration blocked: PlaceAutocompleteElement breaks React controlled inputs (creates own input)
        // Strategy: Monitor deprecation timeline, migrate when React wrappers mature
        // See: /app/docs/project/FUTURE_TASKS.md - "Google Maps Autocomplete API Migration (Phase 2)"
        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            fields: ['formatted_address', 'geometry', 'name', 'address_components'],
            types: ['address']
          }
        );

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();

          if (place.geometry) {
            // Update address (optional - user can edit or leave partial)
            handleInputChange('address', place.formatted_address || place.name);
            
            // Update coordinates
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            handleInputChange('latitude', lat.toString());
            handleInputChange('longitude', lng.toString());
            setCoordinates({ lat, lng });
            
            // Extract location hierarchy (required by backend)
            const locationHierarchy = extractLocationFromAddressComponents(place.address_components);
            handleInputChange('location', locationHierarchy);
            
            toast.success('Location details filled from address!');
          }
        });

        addressAutocompleteRef.current = autocomplete;
        console.log('✅ Address autocomplete initialized');
      } catch (error) {
        console.error('❌ Error initializing address autocomplete:', error);
      }
    }
  }, [modalIsOpen]);

  // Initialize address autocomplete when modal opens

  const suggestedAmenities = [
    'Parking',
    'Water Fountain', 
    'Fenced Area',
    'Benches',
    'Agility Equipment',
    'Waste Bags',
    'Shade/Trees',
    'Off-Leash Area',
    'Small Dog Area',
    'Large Dog Area',
    'Lighting',
    'Restrooms',
    'Walking Trails',
    'Play Equipment',
    'Dog Wash Station',
    'Picnic Tables'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Extract location hierarchy from Google Places address components
  const extractLocationFromAddressComponents = (addressComponents) => {
    if (!addressComponents) return null;
    
    let country = '';
    let city = '';
    let suburb = '';
    let region = '';
    
    addressComponents.forEach(component => {
      const types = component.types;
      
      if (types.includes('country')) {
        country = component.long_name;
      }
      if (types.includes('locality') || types.includes('postal_town')) {
        city = component.long_name;
      }
      if (types.includes('sublocality') || types.includes('neighborhood')) {
        suburb = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        region = component.long_name;
      }
    });
    
    // If no city found, try to use administrative_area_level_2
    if (!city) {
      addressComponents.forEach(component => {
        if (component.types.includes('administrative_area_level_2')) {
          city = component.long_name;
        }
      });
    }
    
    // If still no city, try using administrative_area_level_3
    if (!city) {
      addressComponents.forEach(component => {
        if (component.types.includes('administrative_area_level_3')) {
          city = component.long_name;
        }
      });
    }
    
    // If still no city, use region as fallback
    if (!city && region) {
      city = region;
    }
    
    // Return null only if we don't have at least a country
    if (!country) {
      console.error('❌ Could not extract country from address components:', addressComponents);
      return null;
    }
    
    // If we have country but no city, use a placeholder
    if (!city) {
      console.warn('⚠️ No city found, using region or "Unknown" as fallback');
      city = region || 'Unknown Location';
    }
    
    return {
      country,
      city,
      suburb: suburb || null,
      region: region || null
    };
  };

  const reverseGeocodeLocation = async (lat, lng) => {
    return new Promise((resolve, reject) => {
      try {
        if (!window.google?.maps?.Geocoder) {
          console.error('Google Geocoder not available');
          toast.error('Google Maps not loaded. Please refresh the page.');
          reject(new Error('Google Geocoder not available'));
          return;
        }
        
        const geocoder = new window.google.maps.Geocoder();
        const latlng = { lat, lng };
        
        console.log('🔍 Reverse geocoding:', lat, lng);
        
        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK' && results[0]) {
            console.log('✅ Reverse geocode results:', results[0]);
            
            // Get the formatted address
            const address = results[0].formatted_address;
            handleInputChange('address', address);
            
            // Extract location hierarchy from address components
            const locationHierarchy = extractLocationFromAddressComponents(results[0].address_components);
            
            if (locationHierarchy) {
              handleInputChange('location', locationHierarchy);
              console.log('📍 Location hierarchy from pin:', locationHierarchy);
              toast.success(`Found: ${address}`);
              resolve(locationHierarchy);
            } else {
              toast.warning('Location found, but could not determine city/country. Please try a different location.');
              reject(new Error('Could not extract city/country from location'));
            }
          } else {
            console.error('Reverse geocoding failed:', status);
            toast.error('Could not find address for this location. Please try again.');
            reject(new Error(`Geocoding failed: ${status}`));
          }
        });
      } catch (error) {
        console.error('Reverse geocoding error:', error);
        toast.error('Error getting address');
        reject(error);
      }
    });
  };

  const addAmenity = (amenity) => {
    if (amenity && !formData.amenities.includes(amenity)) {
      handleInputChange('amenities', [...formData.amenities, amenity]);
      setAmenityInput('');
    }
  };

  const removeAmenity = (amenityToRemove) => {
    handleInputChange('amenities', 
      formData.amenities.filter(amenity => amenity !== amenityToRemove)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      toast.error('Please login to add locations');
      return;
    }

    if (!user?.is_verified) {
      toast.error('Please verify your email to add new locations');
      return;
    }

    // Validation
    if (!formData.name.trim()) {
      toast.error('Park name is required');
      return;
    }

    if (!formData.latitude || !formData.longitude) {
      toast.error('Location coordinates are required. Please use the map picker.');
      return;
    }

    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);

    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      toast.error('Please enter valid coordinates');
      return;
    }
    
    if (!formData.location) {
      toast.error('Location details (city/country) are missing. Please wait for location details to load or select the location again from the map.');
      return;
    }

    try {
      setIsLoading(true);

      const locationData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        latitude: lat,
        longitude: lng,
        location: formData.location, // Required: {country, city, suburb, region}
        address: formData.address.trim() || null, // Optional: can be partial like just street name
        amenities: formData.amenities,
        image_url: formData.image_url.trim() || null
      };

      // Add category parameter to URL (admin pin types)
      const categoryParam = formData.pinCategory !== 'standard' ? `?category=${formData.pinCategory}` : '';
      await axios.post(`${BACKEND_URL}/api/parks${categoryParam}`, locationData);

      toast.success('Location added successfully! It will be reviewed by our team.');
      
      // Save the new location coordinates to session storage so map can snap to it
      sessionStorage.setItem('lastMapLocation', JSON.stringify({
        lat: lat,
        lng: lng,
        zoom: 17 // Zoom in close to see the new park
      }));
      console.log('💾 Saved new park location to session storage:', lat, lng);
      
      // Show success modal instead of closing immediately
      setShowSuccessModal(true);
      
      // Trigger celebration animation
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 2000);
      
      // Reset form
      resetForm();
      
      // Callback to refresh data
      if (onLocationAdded) {
        onLocationAdded();
      }
    } catch (error) {
      console.error('Error adding location:', error);
      
      // Handle different error response formats
      let message = 'Failed to add location';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Check if detail is an array of validation errors (Pydantic format)
        if (Array.isArray(detail)) {
          message = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
        } 
        // Check if detail is a string
        else if (typeof detail === 'string') {
          message = detail;
        }
        // Check if detail is an object (single validation error)
        else if (typeof detail === 'object' && detail.msg) {
          message = detail.msg;
        }
      }
      
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      latitude: '',
      longitude: '',
      address: '',
      amenities: [],
      image_url: '',
      location: null,
      pinCategory: 'standard'
    });
    setAmenityInput('');
    setCoordinates(null);
    hasAutoOpenedMapPicker.current = false; // Reset auto-open tracker
  };

  if (!isAuthenticated || !user?.is_verified) {
    return (
      <Dialog open={modalIsOpen} onOpenChange={handleClose}>
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {!isAuthenticated ? 'Authentication Required' : 'Email Verification Required'}
            </DialogTitle>
            <DialogDescription>
              {!isAuthenticated 
                ? 'Please login to add new Paw Park locations to our community.'
                : 'Please verify your email address to add new locations and help grow our community.'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="text-center py-6">
            <p className="text-gray-600 mb-4">
              {!isAuthenticated 
                ? 'Join our community to share amazing Paw Park discoveries!'
                : 'Email verification ensures the quality and trustworthiness of our location data.'
              }
            </p>
            <Button
              onClick={() => {
                setIsOpen(false);
                if (onAuthRequired) {
                  onAuthRequired(); // Trigger redirect modal in parent
                }
              }}
            >
              {!isAuthenticated ? 'Login to Continue' : 'Verify Email First'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={modalIsOpen && !showMapPicker} onOpenChange={(open) => {
        if (!open && !preventMainClose) {
          console.log('🚪 Main modal closing');
          resetForm();
          handleClose();
        } else if (!open && preventMainClose) {
          console.log('✋ Main modal close prevented - exit modal is showing');
        }
      }}>
      {children && (
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-[90vw] sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span>📍</span>
            <span>Add New Happy Paws Location</span>
          </DialogTitle>
          <DialogDescription>
            Share a great paw-friendly location with the community. All submissions are reviewed before going live.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">What is your park's name? *</Label>
              <Input
                id="name"
                type="text"
                placeholder="e.g., Central Paw Park"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                maxLength={200}
                data-testid="location-name-input"
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what makes this park special for dogs and their owners..."
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                maxLength={1000}
                data-testid="location-description-input"
              />
              <div className="flex justify-end text-xs text-gray-500">
                <span>{formData.description.length}/1000</span>
              </div>
            </div>

            {/* Pin Category Selection (Admin Only) */}
            {user && (user.role === 'admin' || user.role === 'master') && (
              <div>
                <Label htmlFor="pinCategory">Pin Type</Label>
                <select
                  id="pinCategory"
                  value={formData.pinCategory}
                  onChange={(e) => handleInputChange('pinCategory', e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  data-testid="pin-category-select"
                >
                  <option value="standard">🐾 Standard Location</option>
                  <option value="featured">⭐ Featured Business</option>
                  <option value="admin_a">🎯 Special Interest</option>
                  <option value="admin_b">🏘️ Community Hub</option>
                  <option value="admin_c">💎 Premium Facility</option>
                  <option value="admin_d">🎪 Event Location</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select the type of pin to display on the map
                </p>
              </div>
            )}
          </div>

          {/* Location Selection - MOVED BEFORE AMENITIES */}
          <div className="space-y-4">
            {/* Map Picker Button */}
            <div>
              <div className="mt-2">
                <Button
                  type="button"
                  onClick={() => {
                    console.log('🗺️ Map button clicked, setting showMapPicker to true');
                    setPreventMainClose(true); // Prevent main modal from closing when map picker is open
                    setShowMapPicker(true);
                    console.log('showMapPicker state updated, preventMainClose set to true');
                  }}
                  className="w-full"
                  variant="outline"
                >
                  🗺️ Choose Park Location on Map
                </Button>
              </div>
              {/* Coordinates hidden for cleaner mobile interface */}
            </div>

            {coordinates && formData.location && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-3">
                  <p className="text-green-800 text-sm font-medium">
                    ✅ Location set: {formData.location.city}, {formData.location.country}
                  </p>
                </CardContent>
              </Card>
            )}
            
            {coordinates && !formData.location && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-3">
                  <p className="text-blue-800 text-sm font-medium">
                    📍 Map ready at your viewing location
                  </p>
                  <p className="text-blue-700 text-xs mt-1">
                    Click "Choose Park Location on Map" below to select the exact pin location
                  </p>
                </CardContent>
              </Card>
            )}
            
            {!coordinates && (
              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="p-3">
                  <p className="text-gray-800 text-sm">
                    📍 Click the button below to select the park location on the map
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Amenities Section - MOVED AFTER LOCATION */}
          <div className="mt-6">
            <Label>Amenities</Label>
            
            {/* Amenities Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 mb-4 mt-3">
              {[
                { name: 'Parking', icon: '🅿️' },
                { name: 'Water Fountain', icon: '💧' },
                { name: 'Fenced Area', icon: '🚧' },
                { name: 'Benches', icon: '🪑' },
                { name: 'Agility Equipment', icon: '🏃' },
                { name: 'Waste Bags', icon: '🗑️' },
                { name: 'Shade/Trees', icon: '🌳' },
                { name: 'Off-Leash Area', icon: '🐕' },
                { name: 'Small Dog Area', icon: '🐕‍🦺' },
                { name: 'Large Dog Area', icon: '🦮' },
                { name: 'Lighting', icon: '💡' },
                { name: 'Restrooms', icon: '🚻' },
                { name: 'Walking Trails', icon: '🥾' },
                { name: 'Play Equipment', icon: '🛝' },
                { name: 'Dog Wash Station', icon: '🚿' },
                { name: 'Picnic Tables', icon: '🍽️' }
              ].map((amenity) => (
                <button
                  key={amenity.name}
                  type="button"
                  onClick={() => {
                    if (formData.amenities.includes(amenity.name)) {
                      removeAmenity(amenity.name);
                    } else {
                      addAmenity(amenity.name);
                    }
                  }}
                  className={`flex flex-col items-center p-2 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                    formData.amenities.includes(amenity.name)
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50'
                  }`}
                >
                  <span className="text-lg mb-0.5">{amenity.icon}</span>
                  <span className="text-[10px] text-center font-medium leading-tight">{amenity.name}</span>
                </button>
              ))}
            </div>
            
            {/* Selected Amenities Display */}
            {formData.amenities.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Selected amenities:</p>
                <div className="flex flex-wrap gap-2">
                  {formData.amenities.map((amenity) => (
                    <Badge 
                      key={amenity} 
                      variant="secondary"
                      className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                    >
                      {amenity}
                      <button
                        type="button"
                        onClick={() => removeAmenity(amenity)}
                        className="ml-1 hover:text-emerald-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Submit Section */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-gray-600">
              Adding as: <span className="font-medium">{user?.username}</span>
              <Badge variant="secondary" className="ml-2 text-xs">Verified ✅</Badge>
            </div>
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isLoading || !formData.name.trim() || !formData.latitude || !formData.longitude}
                data-testid="submit-location-button"
              >
                {isLoading ? 'Adding...' : 'Add Location'}
              </Button>
            </div>
          </div>
        </form>

        {/* Submission Guidelines */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-3">
            <p className="text-blue-800 text-sm">
              <strong>📝 Submission Guidelines:</strong> Please ensure the location is dog-friendly and publicly accessible. All submissions are reviewed by our team before being published to maintain quality and accuracy.
            </p>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>

    {/* Map Picker Modal - Standalone component using Portal */}
    <MapPickerModal
      isOpen={showMapPicker}
      pinCategory={formData.pinCategory}
      onClose={() => {
        setShowMapPicker(false);
        
        // If opened directly from main map, cancel returns to map (close entire modal)
        if (openDirectToMapPicker) {
          console.log('🚫 Map picker closed without placing pin - returning to main map');
          handleClose(); // Close the entire Add Location Modal
        } else {
          // Normal flow: return to Add Location Modal
          setPreventMainClose(true);
          setTimeout(() => {
            setShowExitModal(true);
          }, 100);
        }
      }}
      onConfirm={async (coords) => {
        handleInputChange('latitude', coords.lat.toString());
        handleInputChange('longitude', coords.lng.toString());
        setShowMapPicker(false);
        setPreventMainClose(false);
        
        // Show loading toast while reverse geocoding
        const loadingToastId = toast.loading('Getting location details...');
        
        try {
          // Wait for reverse geocoding to complete before allowing submission
          await reverseGeocodeLocation(coords.lat, coords.lng);
          toast.dismiss(loadingToastId);
          
          // If opened directly from main map, now show the full Add Location Modal
          if (openDirectToMapPicker) {
            console.log('✅ Pin placed - now showing Add Location Modal for details');
            // The modal will automatically show since showMapPicker is now false
            // and modalIsOpen is true
          }
        } catch (error) {
          toast.dismiss(loadingToastId);
          console.error('Reverse geocoding failed:', error);
          // Show error toast with guidance
          toast.error(
            'Could not get location details. Please try placing the pin in a different nearby area with better address coverage.',
            { duration: 5000 }
          );
        }
      }}
      initialCoordinates={coordinates}
      initialZoom={initialMapZoom}
    />

    {/* Success Modal - Shows after successfully adding a location */}
    <Dialog open={showSuccessModal} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent 
        className="sm:max-w-md relative overflow-hidden"  
        style={{ 
          position: 'fixed', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)', 
          zIndex: 9999,
          maxHeight: '90vh'
        }}
      >
        {/* Standardized Close Button */}
        <button
          onClick={() => {
            setShowSuccessModal(false);
            handleClose();
          }}
          className="absolute top-3 right-3 z-50 w-7 h-7 rounded-full flex items-center justify-center text-white font-bold cursor-pointer transition-all duration-200 hover:scale-110"
          style={{
            background: 'linear-gradient(135deg, #8B4513, #A0522D)',
            boxShadow: '0 3px 10px rgba(139, 69, 19, 0.4)',
          }}
          onMouseOver={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #A0522D, #8B4513)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #8B4513, #A0522D)'}
        >
          ×
        </button>
        
        <div className="text-center py-6">
          <div className="text-6xl mb-4">🐾</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Success!</h2>
          <p className="text-gray-600 mb-6">Your location has been added successfully!</p>
          
          {/* Party Popper Celebration Easter Egg */}
          {showCelebration && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="animate-bounce text-4xl">🎉</div>
              <div className="absolute top-4 left-4 animate-ping text-2xl">🎊</div>
              <div className="absolute top-4 right-4 animate-pulse text-2xl">✨</div>
              <div className="absolute bottom-4 left-6 animate-bounce text-xl" style={{animationDelay: '0.2s'}}>🎈</div>
              <div className="absolute bottom-4 right-6 animate-spin text-xl" style={{animationDelay: '0.4s'}}>🌟</div>
              <div className="absolute top-1/2 left-2 animate-pulse text-lg" style={{animationDelay: '0.6s'}}>💫</div>
              <div className="absolute top-1/2 right-2 animate-bounce text-lg" style={{animationDelay: '0.8s'}}>🎯</div>
            </div>
          )}
          
          <div className="space-y-3">
            <Button 
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              onClick={() => {
                setShowSuccessModal(false);
                // Reset to allow adding another location
                resetForm();
              }}
            >
              🐾 Enter Another Location
            </Button>
            
            <Button 
              variant="outline"
              className="w-full bg-amber-50 hover:bg-amber-100 border-amber-300"
              onClick={() => {
                setShowSuccessModal(false);
                handleClose();
                // Navigate to map and set flag to open rating modal
                sessionStorage.setItem('openRatingAfterAdd', 'true');
                navigate('/map');
                toast.success('Taking you to the map to rate your location!');
              }}
            >
              ⭐ Rate This Location
            </Button>
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => {
                setShowSuccessModal(false);
                handleClose();
                navigate('/dashboard');
              }}
            >
              📊 Go to Dashboard
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Exit Modal - Shows when closing map picker without confirming location */}
    {/* Visually similar to success modal, but with different messaging */}
    <Dialog 
      open={showExitModal} 
      onOpenChange={(open) => {
        console.log('🚪 Exit modal onOpenChange:', open);
        setShowExitModal(open);
        if (!open) {
          setPreventMainClose(false); // Re-enable main modal closing
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <div className="text-center py-6">
          <div className="text-6xl mb-4">🐾</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Uh-Oh</h2>
          <p className="text-gray-600 mb-6">Your location was not confirmed - what would you like to do next?</p>
          
          <div className="space-y-3">
            <Button 
              className="w-full bg-emerald-500 hover:bg-emerald-600"
              onClick={() => {
                console.log('🐾 Return to Add Location form');
                setShowExitModal(false);
                setPreventMainClose(false);
                // Reset to allow adding another location
                resetForm();
              }}
            >
              🐾 Enter Another Location
            </Button>
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => {
                console.log('📊 Navigate to dashboard');
                setShowExitModal(false);
                setPreventMainClose(false);
                handleClose();
                navigate('/dashboard');
              }}
            >
              📊 Go to Dashboard
            </Button>
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => {
                console.log('🗺️ Navigate to map page');
                setShowExitModal(false);
                setPreventMainClose(false);
                handleClose();
                navigate('/map');
              }}
            >
              🗺️ Back to Map
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AddLocationModal;